import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactFlow, {
  Background, Controls, MiniMap, useNodesState, useEdgesState, Handle, Position,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  forceSimulation, forceManyBody, forceLink, forceCenter, forceCollide,
} from "d3-force";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { VAULTS, TYPE_CONFIG } from "@/lib/config";

const Icon = ({ name, ...p }) => { const C = Icons[name] || Icons.Circle; return <C {...p} />; };

function AssetNode({ data }) {
  return (
    <div
      className="rounded-md border bg-white shadow-sm px-3 py-2 flex items-center gap-2 cursor-pointer hover:shadow-md transition-shadow"
      style={{ borderColor: data.color, borderLeftWidth: 4, minWidth: 150, maxWidth: 220 }}
      title={data.title}
    >
      <Handle type="target" position={Position.Left} style={{ opacity: 0, border: "none" }} isConnectable={false} />
      <div className="w-7 h-7 shrink-0 rounded-md flex items-center justify-center" style={{ background: `${data.color}18` }}>
        <Icon name={data.icon} className="w-4 h-4" style={{ color: data.color }} />
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold leading-tight truncate">{data.title}</div>
        <div className="text-[10px] uppercase tracking-wide text-gray-400 truncate">{data.typeLabel}</div>
      </div>
      <Handle type="source" position={Position.Right} style={{ opacity: 0, border: "none" }} isConnectable={false} />
    </div>
  );
}
const nodeTypes = { asset: AssetNode };

function layout(rawNodes, rawEdges) {
  const sim = { nodes: rawNodes.map((n) => ({ ...n })), links: rawEdges.map((e) => ({ ...e })) };
  const simulation = forceSimulation(sim.nodes)
    .force("charge", forceManyBody().strength(-600))
    .force("link", forceLink(sim.links).id((d) => d.id).distance(180).strength(0.6))
    .force("center", forceCenter(0, 0))
    .force("collide", forceCollide(90))
    .stop();
  for (let i = 0; i < 400; i++) simulation.tick();
  const pos = {};
  sim.nodes.forEach((n) => { pos[n.id] = { x: n.x, y: n.y }; });
  return pos;
}

export default function CompanyMap() {
  const navigate = useNavigate();
  const [raw, setRaw] = useState({ nodes: [], edges: [] });
  const [vaultFilter, setVaultFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    setLoading(true);
    api.get("/graph").then((r) => setRaw(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!vaultFilter) return raw;
    const keep = new Set(raw.nodes.filter((n) => n.vault === vaultFilter).map((n) => n.id));
    return {
      nodes: raw.nodes.filter((n) => keep.has(n.id)),
      edges: raw.edges.filter((e) => keep.has(e.source) && keep.has(e.target)),
    };
  }, [raw, vaultFilter]);

  useEffect(() => {
    if (filtered.nodes.length === 0) { setNodes([]); setEdges([]); return; }
    const pos = layout(filtered.nodes, filtered.edges);
    const rf = filtered.nodes.map((n) => {
      const vmeta = VAULTS[n.vault] || {};
      const cfg = TYPE_CONFIG[n.type] || {};
      return {
        id: n.id,
        type: "asset",
        position: pos[n.id] || { x: 0, y: 0 },
        data: { title: n.title, typeLabel: cfg.label || n.type, icon: cfg.icon || "Circle", color: vmeta.color || "#6b7280" },
      };
    });
    const re = filtered.edges.map((e, i) => ({
      id: `e${i}`, source: e.source, target: e.target,
      style: { stroke: "#cbd5e1", strokeWidth: 1.5 }, animated: false,
    }));
    setNodes(rf);
    setEdges(re);
  }, [filtered, setNodes, setEdges]);

  const onNodeClick = useCallback((_, node) => navigate(`/item/${node.id}`), [navigate]);

  return (
    <div className="flex flex-col h-[calc(100dvh-3.5rem)] lg:h-screen">
      <header className="p-6 sm:p-8 pb-4 shrink-0">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Relationships</p>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight mt-1">Company Map</h1>
        <p className="text-sm text-gray-500 mt-1">A living model of how your assets connect. Click any node to open it, drag to rearrange, scroll to zoom.</p>

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button onClick={() => setVaultFilter("")} data-testid="map-filter-all"
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${!vaultFilter ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            All vaults
          </button>
          {Object.entries(VAULTS).map(([key, v]) => (
            <button key={key} onClick={() => setVaultFilter(key)} data-testid={`map-filter-${key}`}
              className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5 ${vaultFilter === key ? "text-white border-transparent" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              style={vaultFilter === key ? { background: v.color } : {}}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} /> {v.label}
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 border-t border-gray-200 bg-[#F3F4F6]" data-testid="company-map-canvas">
        {loading ? (
          <div className="h-full flex items-center justify-center text-gray-400">Building company map…</div>
        ) : nodes.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 gap-2">
            <Icons.Share2 className="w-8 h-8" />
            <p className="text-sm">No assets to map yet. Add assets and link them together.</p>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes} onNodeClick={onNodeClick}
            fitView fitViewOptions={{ padding: 0.2 }}
            minZoom={0.2} maxZoom={2} proOptions={{ hideAttribution: true }}
          >
            <Background color="#d1d5db" gap={24} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable nodeColor={(n) => n.data?.color || "#9ca3af"} />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
